import { useMemo, useState } from "react";

import { useFormikWithoutDirtyForm } from "@shared";
import { lastDayOfYear, startOfYear } from "date-fns";

import { SystemDropdownCategory } from "@/api";
import { ALL_COUNSELORS_OPTION_ID } from "@/common/components/counselor-select/FormikCounselorSelect";
import { ColumnFilterOption } from "@/common/components/table/ColumnFilterHeader";
import { useCurrentUserContext } from "@/common/contexts/current-user-context/CurrentUserContext";
import { useSystemDropdownOptions } from "@/common/hooks/useSystemDropdownOptions";
import { toOptionalId } from "@/common/utils/allOption";
import { useTranslation } from "@/translations";

import { ExchangeHistoryFilterValues, ExchangeHistorySearchParams } from "../ExchangeHistoryPanel.types";
import { exchangeHistoryFilterValidationSchema } from "../ExchangeHistoryPanel.utils";
import { useExchangeHistoryQuery } from "./useExchangeHistoryQuery";

/**
 * Page logic for the appointment exchange (Tauschbörse) history. Owns the filter form (date window + counselor
 * dropdowns), loads the matching swaps via the server query, and narrows the loaded rows client-side by the two
 * service column filters. Defaults: the current calendar year, the user's own given-away swaps — or every
 * counselor's for a user without a counselor record — exchanged with anyone.
 */
export function useExchangeHistory() {
  const { t } = useTranslation();
  const { currentUser } = useCurrentUserContext();
  const purposes = useSystemDropdownOptions(SystemDropdownCategory.AppointmentPurpose);
  const [givenServiceFilter, setGivenServiceFilter] = useState<string | null>(null);
  const [receivedServiceFilter, setReceivedServiceFilter] = useState<string | null>(null);

  const validationSchema = useMemo(() => exchangeHistoryFilterValidationSchema(t), [t]);
  const formik = useFormikWithoutDirtyForm<ExchangeHistoryFilterValues>({
    initialValues: {
      fromDate: startOfYear(new Date()),
      tillDate: lastDayOfYear(new Date()),
      givenAwayByCounselorId: currentUser.counselorId ?? ALL_COUNSELORS_OPTION_ID,
      exchangedWithCounselorId: ALL_COUNSELORS_OPTION_ID,
    },
    validationSchema,
    // The filters apply as they change; there is no explicit search step.
    onSubmit: () => undefined,
  });

  // An invalid window fetches nothing: the field error already says why.
  const { fromDate, tillDate, givenAwayByCounselorId, exchangedWithCounselorId } = formik.values;
  const searchParams: ExchangeHistorySearchParams | null = validationSchema.isValidSync(formik.values)
    ? {
        fromDate,
        tillDate,
        givenAwayByCounselorId: toOptionalId(givenAwayByCounselorId, ALL_COUNSELORS_OPTION_ID),
        exchangedWithCounselorId: toOptionalId(exchangedWithCounselorId, ALL_COUNSELORS_OPTION_ID),
      }
    : null;

  const { data, isLoading } = useExchangeHistoryQuery(searchParams);

  const serviceOptions = useMemo<ColumnFilterOption[]>(
    () => purposes.options.map((option) => ({ value: option.key, label: option.label })),
    [purposes.options]
  );

  // While the window is invalid nothing was asked for, and the query's kept previous result must not be
  // shown as if it answered the current filters.
  const hasSearchParams = searchParams !== null;
  const rows = useMemo(
    () =>
      hasSearchParams
        ? data.filter(
            (entry) =>
              (givenServiceFilter === null || entry.givenPurposeKey === givenServiceFilter) &&
              (receivedServiceFilter === null || entry.receivedPurposeKey === receivedServiceFilter)
          )
        : [],
    [hasSearchParams, data, givenServiceFilter, receivedServiceFilter]
  );

  return {
    formik,
    rows,
    isLoading,
    serviceOptions,
    givenServiceFilter,
    setGivenServiceFilter,
    receivedServiceFilter,
    setReceivedServiceFilter,
    paginationResetKey: [
      fromDate?.getTime(),
      tillDate?.getTime(),
      givenAwayByCounselorId,
      exchangedWithCounselorId,
      givenServiceFilter,
      receivedServiceFilter,
    ].join("|"),
  };
}
